import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, viewChild } from '@angular/core';
import { FRANCHISE } from '../content';
import { RevealDirective } from '../reveal.directive';

/**
 * The franchise section, edge to edge above the footer (the header's
 * Franchise link scrolls here): a red field that is never quite still — slow
 * glows, rings spreading out from behind the headline, a light that follows
 * the pointer, and the franchise cities passing by in two rows along the
 * bottom.
 */
@Component({
  selector: 'app-franchise',
  imports: [RevealDirective],
  templateUrl: './franchise.html',
  styleUrl: './franchise.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Franchise {
  protected readonly franchise = FRANCHISE;
  /** the cities pass by in two rows, the second in the opposite order and direction */
  protected readonly rows = [FRANCHISE.cities, [...FRANCHISE.cities].reverse()];
  /** each row is laid out twice end to end, so that it can loop without a seam */
  protected readonly laps = [0, 1];

  private readonly section = viewChild.required<ElementRef<HTMLElement>>('section');

  constructor() {
    const destroyRef = inject(DestroyRef);

    // the light follows a mouse (a finger has no hover to follow)
    afterNextRender(() => {
      if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      const section = this.section().nativeElement;
      let frame = 0;
      let x = 0;
      let y = 0;
      const place = () => {
        frame = 0;
        const box = section.getBoundingClientRect();
        section.style.setProperty('--mx', `${x - box.left}px`);
        section.style.setProperty('--my', `${y - box.top}px`);
      };
      const onMove = (event: PointerEvent) => {
        x = event.clientX;
        y = event.clientY;
        section.classList.add('is-lit');
        frame ||= requestAnimationFrame(place);
      };
      const onLeave = () => section.classList.remove('is-lit');
      section.addEventListener('pointermove', onMove, { passive: true });
      section.addEventListener('pointerleave', onLeave);
      destroyRef.onDestroy(() => {
        section.removeEventListener('pointermove', onMove);
        section.removeEventListener('pointerleave', onLeave);
        cancelAnimationFrame(frame);
      });
    });
  }
}
